// ============================================================================
// CREDIT POINTS SYSTEM
// 1 USD = 20,000 Credit Points
// ============================================================================

export const CREDITS_PER_DOLLAR = 20000;

/**
 * Convert dollars to credit points
 * @param dollars - Amount in dollars
 * @returns Credit points as integer (rounded)
 */
export const dollarsToCredits = (dollars: number | null | undefined): number => {
  if (dollars === null || dollars === undefined) return 0;
  return Math.round(dollars * CREDITS_PER_DOLLAR);
};

/**
 * Convert credit points to dollars
 * @param credits - Amount in credit points
 * @returns Dollars amount
 */
export const creditsToDollars = (credits: number | null | undefined): number => {
  if (credits === null || credits === undefined) return 0;
  return credits / CREDITS_PER_DOLLAR;
};

/**
 * Format credit points with thousands separators
 * @param dollars - Amount in dollars (will be converted to credits)
 * @returns Formatted credit points string
 */
export const formatCredits = (dollars: number | null | undefined): string => {
  const credits = dollarsToCredits(dollars);
  return new Intl.NumberFormat('en-US').format(credits);
};

/**
 * Format credit points with label
 * @param dollars - Amount in dollars (will be converted to credits)
 * @param short - Use short label "CP" instead of "Credit Points"
 * @returns Formatted string like "50,000 Credit Points" or "50,000 CP"
 */
export const formatCreditsWithLabel = (
  dollars: number | null | undefined,
  short: boolean = false
): string => {
  const formatted = formatCredits(dollars);
  return `${formatted} ${short ? 'CP' : 'Credit Points'}`;
};

/**
 * Format a number as currency (legacy - for actual payment displays)
 * @param amount - Amount to format
 * @param currency - Currency code (default: 'USD')
 * @param locale - Locale for formatting (default: 'en-US')
 * @returns Formatted currency string
 */
export const formatCurrency = (
  amount: number | null | undefined,
  currency: string = 'USD',
  locale: string = 'en-US'
): string => {
  if (amount === null || amount === undefined) return '$0.00';

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

/**
 * Format a number with commas as thousands separators
 * @param num - Number to format
 * @param decimalPlaces - Number of decimal places (default: 0)
 * @returns Formatted number string
 */
export const formatNumber = (
  num: number | null | undefined,
  decimalPlaces: number = 0
): string => {
  if (num === null || num === undefined) return '0';
  
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  }).format(num);
};

/**
 * Format a byte size to a human-readable string (e.g., "1.5 MB")
 * @param bytes - Number of bytes
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted size string
 */
export const formatFileSize = (
  bytes: number | null | undefined,
  decimals: number = 2
): string => {
  if (bytes === null || bytes === undefined || bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(decimals))} ${sizes[i]}`;
};
